import { FormProvider } from "react-hook-form";
import classes from "./MedaiLibraryPage.module.less";
import MediaLibrarySearchBox from "./MediaLibrarySearchBox/MediaLibrarySearchBox";
import MediaList from "./MediaList/MediaList";
import Modal from "~/common/components/Modal";
import { Button, CircularProgress, Portal, Stack } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import MediaPreview from "./MediaPreview/MediaPreview";
import type { MediaFilterData, MediaFormFields } from "../types/mediaLibraryFormTypes";
import { useMediaLibrary } from "./useMediaLibrary";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { useIsMinTabletSize } from "../../../common/hooks/useMediaQuery";
import classnames from "classnames";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useCallback } from "react";

interface Props {
  data: MediaFilterData;
}

const MedaiLibraryPage = ({ data }: Props) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();
  const {
    formProps,
    mediaItems,
    selectedMediaItem,
    isOpen,
    loading,
    error,
    stableFilterOptions,
    errorSnackbarOpen,
    loginPopupOpen,
    isDownloading,
    handleModalClose,
    handleCardClick,
    fetchMedia,
    setErrorSnackbarOpen,
    handleFavouriteClick,
    updateMediaItems,
    updateSelectedMediaItem,
    handleDownload,
    openLoginPopup,
    closeLoginPopup,
    clearFavFromSessionStorage,
  } = useMediaLibrary(data);
  const { val: resetPopupOpen, setTrue: openResetPopup, setFalse: closeResetPopup } = useBooleanState(false);

  const onFormSubmit = useCallback(
    (formValues: MediaFormFields) => {
      return fetchMedia({ ...formValues, page: 0 }, true);
    },
    [fetchMedia],
  );

  return (
    <FormProvider {...formProps}>
      <div className={classes.container}>
        <h2 className={classes.title}>{t("media.library.pageTitle")}</h2>
        <MediaLibrarySearchBox filterOptions={stableFilterOptions} submitForm={onFormSubmit} />
        {error ? (
          <Alert severity="error" className={classes.error}>
            {error}
          </Alert>
        ) : (
          <MediaList
            mediaItems={mediaItems}
            loading={loading}
            handleCardClick={handleCardClick}
            handleFavouriteClick={handleFavouriteClick}
            handleDownload={handleDownload}
            isDownloading={isDownloading}
          />
        )}
      </div>
      {isOpen && selectedMediaItem && (
        <Modal
          title={t("media.library.preview.title")}
          onClose={handleModalClose}
          footerClassName={classnames(!isTablet && classes.footerBtnSection)}
          footerChildren={
            <Stack direction="row" gap={1} className={classnames(!isTablet && "w-100")}>
              <Button variant="text" className={classnames(!isTablet && "w-100")} onClick={handleModalClose}>
                {t("close")}
              </Button>
              <Button
                className={classnames(!isTablet && "w-100")}
                disabled={isDownloading}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDownload(selectedMediaItem);
                }}
              >
                {t("media.library.preview.download")}
              </Button>
            </Stack>
          }
        >
          <MediaPreview
            id={selectedMediaItem.id}
            groupId={selectedMediaItem.groupId}
            updateMediaItems={updateMediaItems}
            updateSelectedMediaItem={updateSelectedMediaItem}
            openLoginPopup={openLoginPopup}
          />
        </Modal>
      )}
      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={() => {
            openResetPopup();
            closeLoginPopup();
            clearFavFromSessionStorage();
          }}
          close={() => {
            closeLoginPopup();
            clearFavFromSessionStorage();
          }}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup close={closeResetPopup} initialEmail={""} />}
      <Portal>
        <Snackbar
          open={errorSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setErrorSnackbarOpen(false)}
          action={<SnackbarCloseButton onClick={() => setErrorSnackbarOpen(false)} />}
          message={t("media.library.dowloadFailed")}
        />
        <Snackbar
          open={isDownloading}
          action={<CircularProgress size={20} color={"inherit"} />}
          message={t("media.library.file.downloading")}
        />
      </Portal>
    </FormProvider>
  );
};

export function createMediaLibraryPage(data: MediaFilterData) {
  return <MedaiLibraryPage data={data} />;
}
